import React, { useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";
import { WordReveal } from "./TextEffects";
import {
  Brain, Heart, Rainbow, BookOpen,
  Zap, PawPrint
} from "lucide-react";

/* ─── Primary feature icons ─── */
const primaryFeatures = [
  { key: "personality", icon: Brain, color: "from-orange-500/15 to-amber-500/10", iconColor: "text-orange-600", glowColor: "hsl(25 100% 60% / 0.15)" },
  { key: "memory", icon: BookOpen, color: "from-amber-500/15 to-yellow-500/10", iconColor: "text-amber-600", glowColor: "hsl(40 80% 55% / 0.15)" },
  { key: "emotions", icon: Heart, color: "from-rose-500/15 to-pink-500/10", iconColor: "text-rose-600", glowColor: "hsl(350 70% 55% / 0.15)" },
  { key: "rainbow", icon: Rainbow, color: "from-indigo-500/15 to-violet-500/10", iconColor: "text-indigo-600", glowColor: "hsl(240 50% 60% / 0.15)" },
];

/* ─── FeatureCard3D with spotlight effect ─── */
const FeatureCard3D: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
}> = ({ children, className = "", delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const [hover, setHover] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const spotX = useSpring(mouseX, { stiffness: 200, damping: 30 });
  const spotY = useSpring(mouseY, { stiffness: 200, damping: 30 });

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mouseX.set(e.clientX - rect.left);
    mouseY.set(e.clientY - rect.top);
  };

  return (
    <motion.div
      ref={ref}
      className={`relative ${className}`}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseMove={handleMouseMove}
    >
      <motion.div
        className="h-full relative overflow-hidden"
        animate={hover ? { y: -6 } : { y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          boxShadow: hover
            ? "0 20px 50px -12px hsl(var(--primary) / 0.12), 0 0 0 1px hsl(var(--primary) / 0.08)"
            : "0 1px 3px hsl(var(--foreground) / 0.04), 0 0 0 1px hsl(var(--border) / 0.5)",
          borderRadius: "1.5rem",
        }}
      >
        {/* Spotlight effect */}
        {hover && (
          <motion.div
            className="absolute w-[200px] h-[200px] rounded-full pointer-events-none z-0"
            style={{
              x: spotX,
              y: spotY,
              background: "radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 70%)",
              transform: "translate(-50%, -50%)",
            }}
          />
        )}
        {children}
      </motion.div>
    </motion.div>
  );
};

interface ExtraFeature {
  emoji: string;
  title: string;
  desc: string;
}

export const FeaturesSection: React.FC = () => {
  const { t } = useTranslation();
  const extraFeatures = t('features_extra.items', { returnObjects: true }) as ExtraFeature[];

  // Show only first 8 extra features to avoid wall-of-text
  const visibleExtras = Array.isArray(extraFeatures) ? extraFeatures.slice(0, 8) : [];

  return (
    <section className="py-20 sm:py-28 px-4 relative overflow-hidden" id="features">
      <div className="absolute inset-0 bg-muted/20" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <ScrollReveal className="text-center mb-16">
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-5">
            <Zap size={12} />
            {t('features.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-5">
            <WordReveal
              text={`${t('features.section_title_prefix')} ${t('features.section_title_highlight')}`}
              highlightWords={t('features.section_title_highlight').split(' ')}
            />
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            {t('features.section_subtitle')}
          </p>
        </ScrollReveal>

        {/* Primary features — large cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {primaryFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <FeatureCard3D key={i} delay={i * 0.08}>
                <div className="rounded-3xl p-7 h-full bg-card border border-border/30 relative overflow-hidden group cursor-default">
                  <div className={`absolute inset-0 bg-gradient-to-br ${f.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl`} />
                  <div className="relative z-10">
                    <motion.div
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${f.color} border border-border/30 flex items-center justify-center mb-5`}
                      whileHover={{ rotate: [0, -8, 8, 0], scale: 1.1 }}
                      transition={{ duration: 0.4 }}
                      style={{ boxShadow: `0 8px 24px -6px ${f.glowColor}` }}
                    >
                      <Icon size={24} className={f.iconColor} />
                    </motion.div>
                    <h3 className="font-bold text-foreground text-lg mb-2">{t(`features.${f.key}.title`)}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{t(`features.${f.key}.description`)}</p>
                  </div>
                </div>
              </FeatureCard3D>
            );
          })}
        </div>

        {/* Extra features — compact 2-row grid, max 8 */}
        <ScrollReveal>
          <div className="flex items-center gap-4 justify-center mb-10">
            <div className="h-px flex-1 max-w-24 bg-gradient-to-r from-transparent to-border" />
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
              <PawPrint size={12} className="text-primary" />
              {t('features_extra.section_label')}
            </p>
            <div className="h-px flex-1 max-w-24 bg-gradient-to-l from-transparent to-border" />
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {visibleExtras.map((f, i) => (
            <motion.div
              key={i}
              className="group rounded-2xl bg-card border border-border/30 p-5 cursor-default relative overflow-hidden"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -3, boxShadow: "0 12px 32px -8px hsl(var(--primary) / 0.1)" }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <motion.span
                  className="text-2xl inline-block mb-3"
                  whileHover={{ scale: 1.2, rotate: [0, -8, 8, 0] }}
                  transition={{ duration: 0.3 }}
                >
                  {f.emoji}
                </motion.span>
                <h4 className="font-bold text-sm text-foreground mb-1">{f.title}</h4>
                <p className="text-xs leading-relaxed text-muted-foreground line-clamp-2">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
